import {
  ROTH_CONVERSION_BENEFICIARY_OPTIONS,
  UNIT,
} from '../../../settings/settings';
import { UserBeneficiaries } from '../rothConversionTransaction/types';
import { RothFund } from '../rothConversionTransaction/types';
import { BENEFICIARY_MAP, FUND_MAP } from './rothConversion.util';

/**
 * @param beneficiaries <Beneficiaries[]>
 * @returns {array}
 */
const parseBeneficiaries = (beneficiaries) => {
  return beneficiaries.map((b) => {
    return {
      type: BENEFICIARY_MAP.type[b.type],
      entity: {
        entityName: b.entity?.entityName ?? {
          firstName: b?.firstName || '',
          lastName: b?.lastName || '',
          middleName: b?.middleName || '',
          suffix: b?.suffix || '',
          name:
            BENEFICIARY_MAP.entityType[b.entityType].enumerationName !==
            'Business Entity'
              ? b?.name || ''
              : '',
        },
        entityType: BENEFICIARY_MAP.entityType[b.entityType],
        entityKey: {
          retrieveIDMEmailID: false,
          '@Type': 'EntityKey',
          complete: false,
          idmbusinessPartyID: 0,
          idmemailID: 0,
          derivedEmailID: 0,
          taxID: b.entity?.entityKey?.taxID
            ? b.entity.entityKey.taxID.replace(/-/g, '')
            : b?.taxId
            ? b.taxId.replace(/-/g, '')
            : '',
          johnHancockClientID: 0,
          cifcustomerID: 0,
        },
        names:
          BENEFICIARY_MAP.entityType[b.entityType].enumerationName ===
          'Business Entity'
            ? b.names
            : undefined,
        '@Type': BENEFICIARY_MAP.entityType[
          b.entityType
        ].enumerationName.replace(/ /g, ''),
        dateOfBirth: b.dateOfBirth ? new Date(b.dateOfBirth) : null,
        dateOfBirthString: b.dateOfBirth
          ? b.dateOfBirth.replace(/-/g, '/')
          : '',
      },
      relationship: BENEFICIARY_MAP.relationship[b.relationship],
      asOfDate: new Date(),
      distribution: Number((b.distribution / 100).toFixed(4)),
      designatedDate: new Date(),
      beneficiaryID: 0,
    };
  });
};

/**
 *
 * @param userBeneficiaries <UserBeneficiaries>
 * @return {Beneficiaries[]}
 */
const flattenUserBeneficiaries = (userBeneficiaries: UserBeneficiaries) => {
  const primary = Object.values(userBeneficiaries.primary).map((b) => ({
    ...b,
    type: 'primary',
  }));
  const secondary = Object.values(userBeneficiaries.secondary).map((b) => ({
    ...b,
    type: 'secondary',
  }));

  return [...primary, ...secondary];
};

/**
 * @param funds {Funds[]}
 * @param toFunds {boolean}
 * @return {funds[]}
 */
const parseFunds = ({ funds, taxPercentage, isToFunds }) => {
  const sourceKey = isToFunds ? 'targetPositionKey' : 'sourcePositionKey';
  return Object.values<RothFund>(funds)
    .sort((a, b) =>
      isToFunds
        ? // eslint-disable-next-line
        // @ts-ignore
          a.ogAgreeNum > b.ogAgreeNum
          ? 1
          : // eslint-disable-next-line
          // @ts-ignore
          b.ogAgreeNum > a.ogAgreeNum
          ? -1
          : 0
        : // eslint-disable-next-line
        // @ts-ignore
        a.agreementID > b.agreementID
        ? 1
        : // eslint-disable-next-line
        // @ts-ignore
        b.agreementID > a.agreementID
        ? -1
        : 0
    )
    .map((f) => {
      const agreementNumString = f?.agreementNumber
        ? f?.agreementNumber.split('-')
        : [];
      return {
        subType: FUND_MAP.subType[f.newFund ? 'new' : 'existing'],
        amount: f.closeout && !isToFunds ? undefined : f.grossAmount,
        unit:
          f.closeout && !isToFunds
            ? FUND_MAP.unit[UNIT.ALL_SHARES]
            : FUND_MAP.unit[f.grossUnit || 'DOLLAR'],
        [sourceKey]: {
          agreementKey: {
            productType: {
              enumerationID: 10,
              enumerationName: 'MutualFund',
            },
            '@Type': 'MutualFundAgreementKey',
            agreementID: f?.agreementID,
            // Not sure if agreement number should be null for new funds (from Step 2)
            agreementNumber: agreementNumString[0] || '',
            //checkSum is set to zero if new fund
            checkSum: Number(agreementNumString[1]) || 0,
            trpcode: f?.trpCode,
          },
          investmentVehicleKey: {
            id: 0,
            ticker: f?.ticker,
            rkpcode: f?.rkpcode,
            cusip: f?.cusip,
            '@Type': 'InvestmentVehicleKey',
            trpcode: f?.trpCode,
          },
          '@Type': 'PositionKey',
        },
        '@Type': 'TransactionRecord',
        //jsonClassType: 'com.trp.aar.transfer.transaction.TransactionRecord',
        subjectToIRACloseoutFee: false,
        estimatedRedemptionFee: 0,
        iraWithholding: isToFunds ? 0 : parseFloat(taxPercentage),
      };
    });
};

/**
 * Creates body for roth conversion call
 * @param {Record<string, RothFund>} convertFromFunds
 * @param {Record<string, RothFund>} allocateToFunds
 * @param {number} taxWitholding
 * @param {Beneficiary[]} beneficiaries
 * @param {string} beneficiaryOption
 * @param {UserBeneficiaries} userAddedBeneficiaries
 * @param {boolean} agreeTermsAndConditions
 * @returns {transaction}
 */
export const createRothConversionTransaction = ({
  convertFromFunds,
  allocateToFunds,
  taxPercentage,
  beneficiaries,
  beneficiaryOption,
  userAddedBeneficiaries,
}) => {
  const allBeneficiaries =
    beneficiaryOption === ROTH_CONVERSION_BENEFICIARY_OPTIONS.CREATE_NEW
      ? flattenUserBeneficiaries(userAddedBeneficiaries)
      : beneficiaries;

  return {
    '@Type': 'RothIRAConversionRequest',
    beneficiaries: parseBeneficiaries(allBeneficiaries),
    fromRecords: parseFunds({
      funds: convertFromFunds,
      taxPercentage,
      isToFunds: false,
    }),
    toRecords: parseFunds({
      funds: allocateToFunds,
      taxPercentage,
      isToFunds: true,
    }),
    retailBusinessChannel: {
      enumerationID: 10,
      enumerationName: 'RetailWEB',
    },
    convertFromAccountsAsIs: true,
    consumerName: '',
    consumerRole: '',
  };
};
